import dp from "dialog-polyfill";
import { preventScroll, releaseScroll } from "../Utils/PreventScroll";

export interface IDialog {
    content: string;
    render: () => void;
    close: () => void;
}

export const SELECTORS = {
    TITLE: ":scope .title span:not(.icon)",
    LINK: ":scope a",
    MESSAGE: ":scope .message",
    TYPE_ICON: ":scope .title span.icon",
    CLOSE_ICON: ":scope .icon.close",
    CONTENT: ":scope .content"
};

const CLASSES = {
    DIALOG_OPEN: "dialog-open"
};

export abstract class Dialog implements IDialog {
    #dialog: HTMLDialogElement;

    private preventsScroll: boolean = false;

    protected abstract templateId: string;

    protected abstract modal: boolean;

    private readonly escapeHandler: (e: KeyboardEvent) => void;

    protected abortController: AbortController;
    protected abortSignal: AbortSignal;

    protected eventOpts: { signal: AbortSignal };

    protected constructor() {
        this.abortController = new AbortController();
        this.eventOpts = { signal: this.abortController.signal };
        this.escapeHandler = this.handleEscape.bind(this);
    }

    /**
     * render dialog
     */
    public render(): IDialog {
        document.body.appendChild(this.dialog);
        document.addEventListener("keydown", this.escapeHandler, this.eventOpts);

        // force execution when stack is empty (fixes transition does not apply)
        setTimeout(() => {
            this.dialog[this.modal ? "showModal" : "show"]();
            this.dialog.classList.add("show");
        }, 0);
        return this;
    }

    protected preventScroll() {
        preventScroll(CLASSES.DIALOG_OPEN);
        this.preventsScroll = true;
    }

    protected releaseScroll() {
        if (this.preventsScroll) {
            releaseScroll(CLASSES.DIALOG_OPEN);
            this.preventsScroll = false;
        }
    }

    /**
     * close dialog
     */
    public close() {
        this.abortController.abort();
        this.dialog.addEventListener(
            "transitionend",
            () => {
                this.dialog.dispatchEvent(new CustomEvent("dialog-close", { detail: this }));
                this.dialog.close();
                document.body.removeChild(this.dialog);
                this.releaseScroll();
            },
            { once: true }
        );
        this.dialog.classList.remove("show");
    }

    /**
     * handle escape key
     * @param {KeyboardEvent} e
     * @private
     */
    private handleEscape(e: KeyboardEvent) {
        if (e.key === "Escape") {
            e.preventDefault();
            this.close();
        }
    }

    /**
     * content setter
     * @param {String} v
     */
    public abstract set content(v: string);

    /**
     * obtain dialog element
     */
    get dialog(): HTMLDialogElement {
        if (!this.#dialog) {
            const template = document.getElementById(this.templateId) as HTMLTemplateElement;
            const content = template.content.cloneNode(true) as DocumentFragment;
            this.#dialog = content.querySelector("dialog");
            dp.registerDialog(this.#dialog);
        }
        return this.#dialog;
    }
}
